import { Listing } from "src/types/listing";

interface DemographicInfoProps {
  demographics: NonNullable<Listing['demographics']>;
  variant?: 'detailed' | 'compact';
}

const DemographicInfo = ({ demographics, variant = 'detailed' }: DemographicInfoProps) => {
  const isCompact = variant === 'compact';
  
  // Style classes based on variant
  const titleTextClass = isCompact ? 'text-[8px] font-bold leading-3' : 'text-[24px] font-bold leading-7';
  const headerClass = isCompact ? 'print:mb-0' : 'my-1';
  const gridGapClass = isCompact ? 'gap-6' : 'gap-4';
  const textClass = isCompact ? 'text-[8px] leading-[10px]' : 'text-[14px]';
  const columnGapClass = isCompact ? 'gap-[6px]' : 'gap-2';
  const bottomRowMarginClass = isCompact ? 'mt-1' : 'mt-2';
  
  // Label text based on variant
  const childrenLabel = isCompact ? 'Children:' : 'Number of Children:';
  const homeValueLabel = isCompact ? 'Home Value:' : 'Home Value Range:';

  return (
    <section>
      <div className="">
        <div className={`flex gap-4 h-7 items-center ${headerClass}`}>
          <p className={`whitespace-nowrap ${titleTextClass} text-gray-primary`}>
            Demographic Information{" "}
            <span className="font-[500] text-[12px] text-gray-secondary"></span>
          </p>{" "}
          <div className="h-[2px] w-full bg-gray-300"></div>
        </div>
        <div className={`grid grid-cols-2 ${gridGapClass} ${textClass}`}>
          <div className={`flex flex-col justify-between ${columnGapClass}`}>
            <div className="flex justify-between text-gray-primary">
              <p className="font-semibold">Age:</p>
              <p className="text-right">
                {demographics.age || "--"}
              </p>
            </div>
            <div className="flex justify-between text-gray-primary">
              <p className="font-semibold">Income:</p>
              <p className="text-right">
                {demographics.income 
                  ? `$${demographics.income.toLocaleString()}`
                  : "--"}
              </p>
            </div>
            <div className="flex justify-between text-gray-primary">
              <p className="font-semibold">Occupation:</p>
              <p className="text-right">
                {demographics.occupation || "--"}
              </p>
            </div>
            <div className="flex justify-between text-gray-primary">
              <p className="font-semibold">Education:</p>
              <p className="text-right">
                {demographics.education || "--"}
              </p>
            </div>
          </div>

          <div className={`flex flex-col justify-between ${columnGapClass}`}>
            <div className="flex justify-between text-gray-primary">
              <p className="font-semibold">Gender:</p>
              <p className="text-right">
                {demographics.gender || "--"}
              </p>
            </div>
            <div className="flex justify-between text-gray-primary">
              <p className="font-semibold">Marital Status:</p>
              <p className="text-right">
                {demographics.maritalStatus || "--"}
              </p>
            </div>
            <div className="flex justify-between text-gray-primary">
              <p className="font-semibold">{childrenLabel}</p>
              <p className="text-right">
                {demographics.numberOfChildren ?? "--"}
              </p>
            </div>
            <div className="flex justify-between text-gray-primary">
              <p className="font-semibold">Business Owner:</p>
              <p className="text-right">
                {demographics.businessOwner !== undefined 
                  ? (demographics.businessOwner ? "Yes" : "No")
                  : "--"}
              </p>
            </div>
          </div>
        </div>
        {(demographics.homeValueRange || demographics.netWorth) && (
          <div className={`grid grid-cols-2 ${gridGapClass} ${textClass} ${bottomRowMarginClass}`}>
            <div className="flex justify-between text-gray-primary">
              <p className="font-semibold">{homeValueLabel}</p>
              <p className="text-right">
                {demographics.homeValueRange 
                  ? `$${demographics.homeValueRange.start.toLocaleString()} - $${demographics.homeValueRange.end.toLocaleString()}`
                  : "--"}
              </p>
            </div>
            <div className="flex justify-between text-gray-primary">
              <p className="font-semibold">Net Worth:</p>
              <p className="text-right">
                {demographics.netWorth 
                  ? `$${demographics.netWorth.toLocaleString()}`
                  : "--"}
              </p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default DemographicInfo;